import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/** A feature the platform exposes, with its cross-tenant default. */
export type FeatureDefinition = {
  key: string
  label: string
  description: string
  defaultEnabled: boolean
}

/** A feature definition resolved for one tenant (its effective `enabled`). */
export type FeatureFlagState = {
  key: string
  label: string
  description: string
  defaultEnabled: boolean
  enabled: boolean
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      message = (JSON.parse(text) as { message?: string }).message ?? text
    } catch {
      // keep raw text
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const featureFlagsApi = {
  /**
   * Effective flag map for the current user's tenant (featureKey → enabled).
   * Pass `tenantId` to send it explicitly; otherwise authFetch attaches the
   * selected tenant header by default.
   */
  effective: (tenantId?: string) =>
    request<Record<string, boolean>>(
      '/feature-flags/effective',
      tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : undefined,
    ),

  catalog: () => request<FeatureDefinition[]>('/feature-flags/catalog'),

  listForTenant: (tenantId: string) =>
    request<FeatureFlagState[]>(`/feature-flags/tenants/${tenantId}`),

  setFlag: (tenantId: string, featureKey: string, enabled: boolean) =>
    request<FeatureFlagState>(`/feature-flags/tenants/${tenantId}/${featureKey}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),
}
